"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { BASE_URL } from "../config/apiConfig";
import { toast } from "react-toastify";

const AttendanceContext = createContext();

export const AttendanceProvider = ({ children }) => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [attendanceRule, setAttendanceRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);

  // ✅ Get token from localStorage on mount
  useEffect(() => {
    const localToken = localStorage.getItem("token");
    setToken(localToken);
    if (localToken) fetchAttendance(localToken);
  }, []);

  // Default fetch for current attendance
  const fetchAttendance = async (authToken) => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/attendance`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await response.json();
      if (data.status === 200) {
        setAttendanceData(data.attendance || []);
        setHolidays(data.holidays || []);
        setAttendanceRule(data.attendance_rule || null);
      } else {
        setError(data.message || "Failed to fetch attendance");
        toast.error(data.message || "Failed to fetch attendance");
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      setError(error.message || "Network Error");
      toast.error(error.message || "Network Error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch monthly attendance by MM-YYYY
  const fetchMonthlyAttendance = async (month, year, authToken = token) => {
    if (!authToken) return;
    try {
      setLoading(true);
      setError(null);

      const formattedMonth = String(month + 1).padStart(2, "0");
      const url = `${BASE_URL}/monthly-attendance/${formattedMonth}-${year}`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      if (data.status === 200) {
        setAttendanceData(data.attendance || []);
        setHolidays(data.holidays || []);
        setAttendanceRule(data.attendance_rule || null);
        return data.attendance || [];
      } else {
        setError(data.message || "Failed to fetch monthly attendance");
        toast.error(data.message || "Failed to fetch monthly attendance");
        return null;
      }
    } catch (err) {
      console.error("Monthly Attendance API Error:", err);
      setError(err.message || "Network Error");
      toast.error(err.message || "Network Error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const addManualAttendance = async (payload, authTokenParam) => {
    const authToken = authTokenParam || token; // use passed token or context token

    if (!authToken) {
      return { success: false };
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BASE_URL}/manualattendance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();


      // Backend may return 200 or 201 for success
      if (response.status === 200 || response.status === 201) {
        fetchAttendance(authToken); // refresh attendance list
        toast.success(data.message || "Attendance added successfully");
        return { success: true, data };
      } else {
        console.error("Add Attendance Error:", data.message);
        setError(data.message || "Failed to add attendance");
        toast.error(data.message || "Failed to add attendance");
        return { success: false, data };
      }
    } catch (err) {
      console.error("Add Attendance Exception:", err);
      setError(err.message || "Network Error");
      toast.error(err.message || "Network Error");
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const sendAttendanceMail = async (payload, authTokenParam) => {
    const authToken = authTokenParam || token;

    if (!authToken) return { success: false };

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${BASE_URL}/attendance/send-mail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.status === 200 || data.status === 200) {
        // 🔥 refresh attendance so mail_sent updates
        await fetchMonthlyAttendance(
          new Date().getMonth(),
          new Date().getFullYear(),
          authToken
        );

        toast.success(data.message || "Mail sent successfully");
        return { success: true, data };
      } else {
        setError(data.message || "Failed to send mail");
        toast.error(data.message || "Failed to send mail");
        return { success: false, data };
      }
    } catch (err) {
      setError(err.message || "Network Error");
      toast.error(err.message || "Network Error");
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  return (
    <AttendanceContext.Provider
      value={{
        attendanceData,
        holidays,
        attendanceRule,
        loading,
        token,
        error,
        fetchAttendance,
        fetchMonthlyAttendance,
        addManualAttendance,
        sendAttendanceMail,
      }}
    >
      {children}
    </AttendanceContext.Provider>
  );
};

export const useAttendance = () => useContext(AttendanceContext);